import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ───────────────────────────────────────────────────────────────

export interface Contact {
  id: string;
  org_id: string;
  type: "person" | "organization";
  organization_id: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  role: string | null;
  email: string | null;
  phone: string | null;
  phone_ext: string | null;
  phone_mobile: string | null;
  website: string | null;
  address: string | null;
  tags: string[];
  visible_to: string[] | null;
  notes: string | null;
  avatar_url: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateContactInput {
  type: "person" | "organization";
  organization_id?: string | null;
  first_name?: string;
  last_name?: string;
  company?: string;
  role?: string;
  email?: string;
  phone?: string;
  phone_ext?: string;
  phone_mobile?: string;
  website?: string;
  address?: string;
  tags?: string[];
  visible_to?: string[] | null;
  notes?: string;
}

export interface UpdateContactInput {
  type?: "person" | "organization";
  organization_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  phone_ext?: string | null;
  phone_mobile?: string | null;
  website?: string | null;
  address?: string | null;
  tags?: string[];
  visible_to?: string[] | null;
  notes?: string | null;
  avatar_url?: string | null;
}

export interface ContactTag {
  id: string;
  org_id: string;
  tag: string;
  label: string;
  description: string | null;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ── Queries ─────────────────────────────────────────────────────────────

/**
 * List contacts with optional search, tag filter, type filter, and archive toggle.
 * Uses the search_contacts RPC for full-text search, tag filtering, and archive support.
 */
export function useContacts(
  searchTerm?: string,
  tagFilter?: string[],
  typeFilter?: "person" | "organization" | null,
  showArchived?: boolean
) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["contacts", searchTerm, tagFilter, typeFilter, showArchived],
    queryFn: async () => {
      // Use RPC when we have search, tag filter, or archive toggle
      if (searchTerm || (tagFilter && tagFilter.length > 0) || showArchived) {
        const { data, error } = await supabase.rpc("search_contacts", {
          p_search_term: searchTerm || null,
          p_filter_tags: tagFilter && tagFilter.length > 0 ? tagFilter : null,
          p_type: typeFilter || null,
          p_include_archived: showArchived || false,
        });
        if (error) throw error;
        return (data || []) as Contact[];
      }

      // Standard query for unfiltered listing
      let query = supabase
        .from("contact" as any)
        .select("*")
        .eq("is_active", true)
        .order("last_name", { ascending: true })
        .order("first_name", { ascending: true })
        .order("company", { ascending: true });

      if (typeFilter) {
        query = query.eq("type", typeFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Contact[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

/** Fetch a single contact by ID. */
export function useContact(id: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["contacts", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact" as any)
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as Contact;
    },
    enabled: !!id && isAuthenticated && !authLoading,
  });
}

/** Fetch people linked to a given organization contact. */
export function useContactsByOrganization(orgContactId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["contacts", "by-org", orgContactId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact" as any)
        .select("*")
        .eq("organization_id", orgContactId!)
        .eq("is_active", true)
        .order("last_name", { ascending: true });
      if (error) throw error;
      return (data || []) as Contact[];
    },
    enabled: !!orgContactId && isAuthenticated && !authLoading,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────

/** Create a new contact. */
export function useCreateContact() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateContactInput) => {
      // Get org_id from current user
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id, id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("contact" as any)
        .insert([{
          type: input.type,
          organization_id: input.organization_id || null,
          first_name: input.first_name || null,
          last_name: input.last_name || null,
          company: input.company || null,
          role: input.role || null,
          email: input.email || null,
          phone: input.phone || null,
          phone_ext: input.phone_ext || null,
          phone_mobile: input.phone_mobile || null,
          website: input.website || null,
          address: input.address || null,
          tags: input.tags || [],
          visible_to: input.visible_to || null,
          notes: input.notes || null,
          org_id: userData.org_id,
          created_by: userData.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as Contact;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      const label = data.type === "organization"
        ? (data.company || "Organization")
        : [data.first_name, data.last_name].filter(Boolean).join(" ") || "Contact";
      toast({
        title: "Contact created",
        description: `${label} has been added to your contacts.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating contact",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/** Update an existing contact. */
export function useUpdateContact() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateContactInput }) => {
      const { data, error } = await supabase
        .from("contact" as any)
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Contact;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({
        title: "Contact updated",
        description: "The contact has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating contact",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/** Soft-delete (archive) a contact. */
export function useArchiveContact() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("contact" as any)
        .update({ is_active: false })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Contact;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      const label = data.type === "organization"
        ? (data.company || "Organization")
        : [data.first_name, data.last_name].filter(Boolean).join(" ") || "Contact";
      toast({
        title: "Contact archived",
        description: `${label} has been archived.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error archiving contact",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/** Restore an archived contact. */
export function useRestoreContact() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("contact" as any)
        .update({ is_active: true })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Contact;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      const label = data.type === "organization"
        ? (data.company || "Organization")
        : [data.first_name, data.last_name].filter(Boolean).join(" ") || "Contact";
      toast({
        title: "Contact restored",
        description: `${label} has been restored.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error restoring contact",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

// ── Tags ────────────────────────────────────────────────────────────────

/** Fetch all active contact tags for the current org. */
export function useContactTags() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["contact-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact_tag" as any)
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return (data || []) as ContactTag[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

/** Fetch organization-type contacts for the parent org picker. */
export function useOrganizationContacts() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["contacts", "organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contact" as any)
        .select("id, company")
        .eq("type", "organization")
        .eq("is_active", true)
        .order("company", { ascending: true });

      if (error) throw error;
      return (data || []) as Pick<Contact, "id" | "company">[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

// ── Avatar Upload ───────────────────────────────────────────────────────

/** Upload a contact avatar and update the contact record. */
export function useUploadContactAvatar() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contactId, file }: { contactId: string; file: File }) => {
      const fileExt = file.name.split(".").pop();
      const fileName = `${contactId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("contact-avatars")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("contact-avatars")
        .getPublicUrl(fileName);

      // Update the contact record with the new avatar URL
      const { data, error } = await supabase
        .from("contact" as any)
        .update({ avatar_url: urlData.publicUrl })
        .eq("id", contactId)
        .select()
        .single();

      if (error) throw error;
      return data as Contact;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({
        title: "Avatar uploaded",
        description: "The contact photo has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error uploading avatar",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
